import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import ApiService from '../services/api';

// Site settings interfaces
interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}

interface ThemeFonts {
  heading: string;
  body: string;
  headingSize: number;
  bodySize: number;
}

interface ThemeGradient {
  enabled: boolean;
  startColor: string;
  endColor: string;
  angle: number;
}

interface ThemeSettings {
  colors: ThemeColors;
  gradient?: ThemeGradient;
  fonts: ThemeFonts;
}

interface WelcomeContent {
  title: string;
  message: string;
  tagline: string;
}

interface SocialLinks {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  custom?: Array<{
    name: string;
    url: string;
    icon?: string;
  }>;
}

interface FooterContent {
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  copyrightText?: string;
  socialLinks: SocialLinks;
}

interface ContentSettings {
  welcome: WelcomeContent;
  footer: FooterContent;
}

interface ImageItem {
  url: string;
  alt: string;
}

interface BackgroundImage extends ImageItem {
  opacity: number;
}

interface ImageSettings {
  logo?: ImageItem;
  hero?: ImageItem;
  background?: BackgroundImage;
}

interface SiteSettings {
  theme: ThemeSettings;
  content: ContentSettings;
  images: ImageSettings;
  customCSS?: string;
}

interface CustomThemeContextValue {
  siteSettings: SiteSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

// Default theme settings
const DEFAULT_SETTINGS: SiteSettings = {
  theme: {
    colors: {
      primary: '#1976d2',
      secondary: '#dc004e',
      accent: '#f50057',
      background: '#ffffff',
      text: '#000000'
    },
    fonts: {
      heading: 'Roboto',
      body: 'Roboto',
      headingSize: 24,
      bodySize: 16
    }
  },
  content: {
    welcome: {
      title: 'Welcome to Our Event',
      message: 'We\'re excited to have you join us!',
      tagline: 'Making Memories Together'
    },
    footer: {
      contactEmail: 'info@example.com',
      copyrightText: `© ${new Date().getFullYear()} All Rights Reserved`,
      socialLinks: {}
    }
  },
  images: {},
  customCSS: ''
};

// Create context
const CustomThemeContext = createContext<CustomThemeContextValue>({
  siteSettings: null,
  loading: true,
  error: null,
  refreshSettings: async () => {}
});

// Hook to use the custom theme context
export const useCustomTheme = () => {
  const context = useContext(CustomThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a CustomThemeProvider');
  }
  return context;
};

interface CustomThemeProviderProps {
  children: ReactNode;
}

// CSS sanitization function
const sanitizeCSS = (css: string): string => {
  if (!css) return '';
  
  // Remove script tags and event handlers
  let sanitized = css.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove @import and external URLs (basic protection)
  sanitized = sanitized.replace(/@import\s+url\([^)]*\)/gi, '');
  sanitized = sanitized.replace(/url\(\s*["']?https?:\/\/[^)"']*["']?\s*\)/gi, '');
  
  return sanitized;
};

// Extract critical CSS (above-the-fold styles)
const extractCriticalCSS = (css: string): string => {
  if (!css) return '';
  
  const criticalSelectors = [
    'body', 'html',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'a',
    '.header', '.nav', '.hero',
    '#root', '#app'
  ];

  const rules = css.split('}').filter(rule => rule.trim());
  const criticalRules: string[] = [];

  rules.forEach(rule => {
    const trimmedRule = rule.trim();
    if (!trimmedRule) return;

    const parts = trimmedRule.split('{');
    if (parts.length !== 2) return;

    const selector = parts[0].trim();

    // Check if selector matches any critical selector
    const isCritical = criticalSelectors.some(criticalSel => 
      selector.includes(criticalSel)
    );

    if (isCritical) {
      criticalRules.push(trimmedRule + '}');
    }
  });

  return criticalRules.join('\n');
};

// Inject custom CSS into page head with critical CSS inlining
const injectCustomCSS = (css: string, elementId: string = 'custom-site-css') => {
  // Remove existing custom CSS if present
  const existingStyle = document.getElementById(elementId);
  if (existingStyle) {
    existingStyle.remove();
  }
  
  const existingCritical = document.getElementById(`${elementId}-critical`);
  if (existingCritical) {
    existingCritical.remove();
  }
  
  if (!css) return;
  
  try {
    // Sanitize CSS
    const sanitizedCSS = sanitizeCSS(css);
    
    // Extract and inline critical CSS for faster initial render
    const criticalCSS = extractCriticalCSS(sanitizedCSS);
    if (criticalCSS) {
      const criticalStyle = document.createElement('style');
      criticalStyle.id = `${elementId}-critical`;
      criticalStyle.textContent = criticalCSS;
      // Insert at the beginning of head for highest priority
      document.head.insertBefore(criticalStyle, document.head.firstChild);
    }
    
    // Inject remaining CSS
    const styleElement = document.createElement('style');
    styleElement.id = elementId;
    styleElement.textContent = sanitizedCSS;
    // Use media attribute to defer non-critical CSS
    styleElement.setAttribute('media', 'print');
    document.head.appendChild(styleElement);
    
    // Change media to 'all' after a short delay to apply non-critical styles
    setTimeout(() => {
      styleElement.setAttribute('media', 'all');
    }, 0);
  } catch (error) {
    console.error('Error injecting custom CSS:', error);
  }
};

// Generate MUI theme from site settings
const generateMuiTheme = (settings: SiteSettings): Theme => {
  const { theme } = settings;
  
  return createTheme({
    palette: {
      primary: {
        main: theme.colors.primary,
      },
      secondary: {
        main: theme.colors.secondary,
      },
      background: {
        default: theme.colors.background,
        paper: theme.colors.background,
      },
      text: {
        primary: theme.colors.text,
      },
    },
    typography: {
      fontFamily: `"${theme.fonts.body}", "Roboto", "Helvetica", "Arial", sans-serif`,
      h1: {
        fontFamily: `"${theme.fonts.heading}", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: `${theme.fonts.headingSize * 1.5}px`,
        fontWeight: 700,
      },
      h2: {
        fontFamily: `"${theme.fonts.heading}", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: `${theme.fonts.headingSize * 1.3}px`,
        fontWeight: 600,
      },
      h3: {
        fontFamily: `"${theme.fonts.heading}", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: `${theme.fonts.headingSize * 1.1}px`,
        fontWeight: 600,
      },
      h4: {
        fontFamily: `"${theme.fonts.heading}", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: `${theme.fonts.headingSize}px`,
        fontWeight: 600,
      },
      h5: {
        fontFamily: `"${theme.fonts.heading}", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: `${theme.fonts.headingSize * 0.9}px`,
        fontWeight: 600,
      },
      h6: {
        fontFamily: `"${theme.fonts.heading}", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: `${theme.fonts.headingSize * 0.8}px`,
        fontWeight: 600,
      },
      body1: {
        fontSize: `${theme.fonts.bodySize}px`,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: `${theme.fonts.bodySize * 0.875}px`,
        lineHeight: 1.5,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
    },
  });
};

// Apply CSS variables to document root
const applyCSSVariables = (settings: SiteSettings) => {
  const root = document.documentElement;
  const { theme } = settings;
  
  // Set color variables
  root.style.setProperty('--color-primary', theme.colors.primary);
  root.style.setProperty('--color-secondary', theme.colors.secondary);
  root.style.setProperty('--color-accent', theme.colors.accent);
  root.style.setProperty('--color-background', theme.colors.background);
  root.style.setProperty('--color-text', theme.colors.text);
  
  // Set gradient variables
  if (theme.gradient?.enabled) {
    const gradientValue = `linear-gradient(${theme.gradient.angle}deg, ${theme.gradient.startColor} 0%, ${theme.gradient.endColor} 100%)`;
    root.style.setProperty('--gradient-background', gradientValue);
    root.style.setProperty('--gradient-enabled', 'true');
  } else {
    root.style.setProperty('--gradient-background', 'none');
    root.style.setProperty('--gradient-enabled', 'false');
  }
  
  // Set font variables
  root.style.setProperty('--font-heading', theme.fonts.heading);
  root.style.setProperty('--font-body', theme.fonts.body);
  root.style.setProperty('--font-size-heading', `${theme.fonts.headingSize}px`);
  root.style.setProperty('--font-size-body', `${theme.fonts.bodySize}px`);
};

// Cache configuration
const CACHE_KEY = 'cached-site-settings';
const CACHE_VERSION_KEY = 'cached-site-settings-version';
const CACHE_TIMESTAMP_KEY = 'cached-site-settings-timestamp';
const CACHE_TTL = 3600000; // 1 hour in milliseconds

// Check if cached settings are still valid
const isCacheValid = (): boolean => {
  try {
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const cacheAge = Date.now() - parseInt(timestamp, 10);
    return cacheAge < CACHE_TTL;
  } catch {
    return false;
  }
};

// Get cached settings if valid
const getCachedSettings = (): SiteSettings | null => {
  try {
    if (!isCacheValid()) {
      return null;
    }
    
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    return JSON.parse(cached);
  } catch (err) {
    console.error('Error reading cached settings:', err);
    return null;
  }
};

// Save settings to cache with version and timestamp
const cacheSettings = (settings: SiteSettings, version?: string) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    if (version) {
      localStorage.setItem(CACHE_VERSION_KEY, version);
    }
  } catch (err) {
    console.error('Error caching settings:', err);
  }
};

// Invalidate cache (called after publish)
export const invalidateSiteSettingsCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_VERSION_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch (err) {
    console.error('Error invalidating cache:', err);
  }
};

export const CustomThemeProvider: React.FC<CustomThemeProviderProps> = ({ children }) => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [muiTheme, setMuiTheme] = useState<Theme>(createTheme());

  const applySettings = (settings: SiteSettings) => {
    setSiteSettings(settings);
    
    // Generate and apply MUI theme
    const newTheme = generateMuiTheme(settings);
    setMuiTheme(newTheme);
    
    // Apply CSS variables
    applyCSSVariables(settings);
    
    // Inject custom CSS
    if (settings.customCSS) {
      injectCustomCSS(settings.customCSS);
    }
  };

  const fetchSettings = async (useCache: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to use cached settings first if enabled
      if (useCache) {
        const cached = getCachedSettings();
        if (cached) {
          console.log('Using cached site settings');
          applySettings(cached);
          setLoading(false);
          
          // Fetch fresh settings in background to update cache
          fetchSettings(false).catch(err => {
            console.warn('Background settings refresh failed:', err);
          });
          return;
        }
      }
      
      // Fetch published settings from public endpoint
      const response = await ApiService.getPublicSiteSettings();
      
      if (response.success && response.data) {
        const settings: SiteSettings = {
          theme: response.data.theme || DEFAULT_SETTINGS.theme,
          content: response.data.content || DEFAULT_SETTINGS.content,
          images: response.data.images || DEFAULT_SETTINGS.images,
          customCSS: response.data.customCSS || ''
        };
        
        applySettings(settings);
        
        // Cache settings with version if available
        const version = response.data.version || response.headers?.etag;
        cacheSettings(settings, version);
      } else {
        // Use default settings
        applySettings(DEFAULT_SETTINGS);
      }
    } catch (err) {
      console.error('Error fetching site settings:', err);
      setError('Failed to load site customization');
      
      // Try to use cached settings even if expired
      const cachedSettings = getCachedSettings();
      if (cachedSettings) {
        console.log('Using expired cache as fallback');
        applySettings(cachedSettings);
      } else {
        // Use default settings as final fallback
        applySettings(DEFAULT_SETTINGS);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const refreshSettings = async () => {
    // Force refresh without using cache
    await fetchSettings(false);
  };

  const contextValue: CustomThemeContextValue = {
    siteSettings,
    loading,
    error,
    refreshSettings
  };

  return (
    <CustomThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </CustomThemeContext.Provider>
  );
};
